import { Cylinder, Heart } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "../utils/functions";
import api from "../lib/api.ts";
import BidInformation from "./BidInformation";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useLanguage } from "../context/LanguageContext";
import placeholder from "../assets/Car-place-holder-PNG.png";
import { useCurrencyUtils } from "../utils/currencyUtils";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const DUBAI_TZ = "Asia/Dubai";

const VehicleCard = ({
  vehicle,
  wishlistData = [],
  auctionLaneId: propAuctionLaneId,
  auction: propAuction,
}) => {
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const textDirection = isRTL ? "rtl" : "ltr";
  const directionalRowClass = isRTL ? "flex-row-reverse text-right" : "";

  // ────────────────────────────────────────────────
  // Single destructuring – no duplicate declarations
  // ────────────────────────────────────────────────
  const {
    id,
    name,
    title: vehicleTitle,
    slug,
    lotNumber,
    lotNo: propLotNo,
    auctionLanesVehicles,
    primaryDamageType: condition = "Unknown",
    mileage,
    miles: propMiles,
    fuelType,
    engineSize,
    cylinder,
    transmissionType: transmission,
    driveLineType,
    DriveLineType: apiDriveLineType,
    bodyType,
    BodyType: apiBodyType,
    images,
    startingBid = 0,
    latestBid: currentBid = 0,
    bidIncrement = 100,
    buyNowPrice = 0,
    buyNowEndTime,
    highlight,
    highlights: propHighlights,
    ProductStatus: productStatus,
    keys: isRunAndDrive = false,
    specifications,
    exteriorColor,
    interiorColor,
    runNo: propRunNo,
  } = vehicle || {};
  // console.log("Vehicle Data:", vehicle);
  const effectiveBodyType = bodyType || apiBodyType;
  const effectiveDriveLineType = driveLineType || apiDriveLineType;

  // console.log("Body Type:", effectiveBodyType?.name || "-");
  // console.log("Drive Line Type:", effectiveDriveLineType?.name || "-");
 

  const title = name || vehicleTitle || "Unknown Vehicle";
  const lotNo = propLotNo || lotNumber;
  const miles = propMiles || mileage || vehicle?.mileage || 0;
  const rawHighlights = Array.isArray(propHighlights) ? propHighlights : (highlight ? [highlight] : []);
  const highlights = rawHighlights
    .map(h => typeof h === 'string' ? { name: h, bgColor: "#135E9F", textColor: "#ffffff" } : h)
    .filter(h => h && h.name);

  // Computed / fallback values
  const runNo = propRunNo || auctionLanesVehicles?.[0]?.runNo || vehicle?.runNo;
  const auctionLaneId =
    propAuctionLaneId ||
    auctionLanesVehicles?.[0]?.auctionLane?.id ||
    auctionLanesVehicles?.[0]?.id;

  const auction =
    propAuction ||
    vehicle?.auction ||
    auctionLanesVehicles?.[0]?.auctionLane?.auction;

  // const imageUrls = (images || [])
  //   .map((img) =>
  //     typeof img === "string"
  //       ? img
  //       : img?.imageUrl || img?.url || img?.path || img?.src || "",
  //   )
  //   .filter(Boolean);

  // const image = imageUrls.length > 0 ? imageUrls[0] : (vehicle?.imageUrl || placeholder);
  const image = vehicle?.images?.[0]?.imageUrl || vehicle?.image || placeholder
  // console.log("Vehicle Images:", vehicle?.image);
  const latestBid = currentBid || startingBid || 0;

  // Currency utils
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showBidNowPopup, setShowBidNowPopup] = useState(false);
  const [bidAmount, setBidAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatedVehicle, setUpdatedVehicle] = useState(vehicle);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);

  // Wishlist check
  useEffect(() => {
    if (!user?.id || !id || !Array.isArray(wishlistData)) return;
    const isInWishlist = wishlistData.some((item) => item.vehicleId === id);
    setIsBookmarked(isInWishlist);
  }, [user?.id, id, wishlistData]);

  // Prebid & Buy Now timers
  const [prebidTimeLeft, setPrebidTimeLeft] = useState(() => {
    const endDate = vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.preBidEndDate;
    if (!endDate) return 0;
    return Math.max(0, new Date(endDate).getTime() - Date.now());
  });

  const [buyNowTimeLeft, setBuyNowTimeLeft] = useState(() => {
    if (!buyNowEndTime) return 0;
    return Math.max(0, new Date(buyNowEndTime).getTime() - Date.now());
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setPrebidTimeLeft((prev) => Math.max(0, prev - 1000));
      setBuyNowTimeLeft((prev) => Math.max(0, prev - 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Auction / vehicle status checks
  const isFuture = !auctionLaneId;
  const isVehicleSold =
    productStatus === "Sold" ||
    vehicle?.ProductStatus?.name === "Sold" ||
    vehicle?.ProductAuctionStatus?.name === "Sold";

  const isBuyNowVehicle = productStatus === "Buy Now" || buyNowPrice > 0;
  const isBuyNowExpired = isBuyNowVehicle && buyNowTimeLeft <= 0;

  // const isAuctionLive = () => {
  //   if (!vehicle?.auction) return false;
  //   const now = dayjs().tz(DUBAI_TZ);
  //   const start = dayjs.utc(vehicle.auction.startDate).tz(DUBAI_TZ);
  //   const end = vehicle.auction.endDate
  //     ? dayjs.utc(vehicle.auction.endDate).tz(DUBAI_TZ)
  //     : null;

  //   const isStarted = now.isAfter(start) || now.isSame(start);
  //   const isEnded = end ? now.isAfter(end) : false;

  //   return isStarted && !isEnded;
  // };
  const isAuctionLive = () => {
    if (!auction || !auction.startDate) return false;

    const now = dayjs().tz(DUBAI_TZ);

    const start = dayjs(auction.startDate).tz(DUBAI_TZ);
    const end = auction.endDate
      ? dayjs(auction.endDate).tz(DUBAI_TZ)
      : null;

    const isStarted = now.isAfter(start) || now.isSame(start);
    const isEnded = end ? now.isAfter(end) : false;

    return isStarted && !isEnded;
  };

  const isLive = isAuctionLive();

  // Wishlist toggle
  const toggleWishlist = async () => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    try {
      const res = await api.post("/wishlist/toggle", { vehicleId: id });
      if (res.data.success) {
        setIsBookmarked((prev) => !prev);
        showSuccess(
          isBookmarked
            ? translate("vehicleCard.messages.wishlistRemoved")
            : translate("vehicleCard.messages.wishlistSuccess")
        );
      } else {
        showError(translate("vehicleCard.messages.wishlistFailed"));
      }
    } catch (err) {
      console.error("Wishlist toggle failed", err);
      showError(translate("vehicleCard.messages.wishlistFailed"));
    }
  };

  // Bid Now handlers
  const handleBidNow = () => {
    const base = latestBid > 0 ? latestBid : startingBid;
    setBidAmount(base + bidIncrement);
    setShowBidNowPopup(true);
  };

  const handleBidNowSubmit = async () => {
    setIsSubmitting(true);
    try {
      let finalAuctionLaneId =
        auctionLaneId ||
        vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.id ||
        vehicle?.auctionLanesVehicles?.[0]?.id;

      if (!finalAuctionLaneId || !id || !bidAmount || bidAmount <= 0) {
        showError(translate("vehicleCard.messages.invalidBidData"));
        return;
      }

      const res = await api.post("/bidding/prebid", {
        auctionLaneId: finalAuctionLaneId,
        vehicleId: id,
        amount: bidAmount,
      });

      if (res.data.success) {
        const newBid = Number(bidAmount);
        setBidAmount(newBid + bidIncrement);
        setUpdatedVehicle((prev) => ({
          ...prev,
          latestBid: newBid.toString(),
          highestBidderId: user?.id,
        }));
        showSuccess(translate("vehicleCard.messages.preBidSuccess"));
      } else {
        showError(
          res.data.message || translate("vehicleCard.messages.preBidFailed")
        );
      }
    } catch (err) {
      console.error("Pre-bid error:", err);
      showError(translate("vehicleCard.messages.preBidFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeBidNowPopup = () => setShowBidNowPopup(false);

  // Buy Now handlers
  const handleBuyNow = () => {
    if (!user?.id) return navigate("/login");
    if (user?.userType === "seller") {
      showError(translate("vehicleCard.messages.buyerOnly"));
      return;
    }
    setShowBuyNowModal(true);
  };

  const handleBuyNowConfirm = async () => {
    try {
      const buyerId = user?.buyer?.id;
      if (!buyerId) throw new Error("Buyer ID missing");

      const res = await api.post("/bidding/marksoldfrombuynow", {
        vehicleId: id,
        buyerId,
        soldPrice: buyNowPrice,
      });

      if (res.data.success) {
        showSuccess(translate("vehicleCard.messages.purchaseSuccess"));
        setShowBuyNowModal(false);
        setTimeout(() => window.location.reload(), 1800);
      } else {
        showError(
          res.data.message || translate("vehicleCard.messages.purchaseFailed")
        );
      }
    } catch (error) {
      console.error("Error confirming Buy Now purchase:", error);
      showError(
        error.response?.data?.message ||
        translate("vehicleCard.messages.purchaseFailed")
      );
    }
  };

  const handleBuyNowModalClose = () => setShowBuyNowModal(false);

  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return translate("vehicleCard.labels.expired");

    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  // ────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────
  return (
    <>
      <div
        className="border bg-[#1C262E] border-default rounded-lg overflow-hidden hover:scale-105 hover:shadow-2xl transition-transform duration-300 ease-out"
        dir={textDirection}
        lang={language}
      >
        {/* Image Section */}
        <div
          className="relative cursor-pointer aspect-[4/3]"
          onClick={() => navigate(`/${slug}`)}
        >
          {/* <img
            src={image}
            alt={title}
            className={`w-full h-full object-cover ${image === placeholder ? "opacity-50" : ""}`}
            onError={(e) => (e.target.src = placeholder)}
          /> */}
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = placeholder;
            }}
          />

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className={`absolute top-2 ${isRTL ? "right-1" : "left-1"} flex flex-wrap gap-1 max-w-[70%] z-10`}>
              {highlights.slice(0, 3).map((h, i) => (
                <div
                  key={i}
                  className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider shadow-sm"
                  style={{ backgroundColor: h.bgColor, color: h.textColor }}
                >
                  {h.name}
                </div>
              ))}
              {highlights.length > 3 && (
                <div className="bg-gray-800 text-white px-2 py-1 rounded text-[10px] font-bold shadow-sm">
                  +{highlights.length - 3}
                </div>
              )}
            </div>
          )}

          {/* Bookmark */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleWishlist();
            }}
            className={`absolute top-2 ${isRTL ? "left-2" : "right-2"} rounded-full p-2 transition-all duration-200 z-10 ${isBookmarked
              ? "bg-primary text-white hover:bg-primary-hover"
              : "bg-white bg-opacity-80 text-black hover:bg-primary hover:text-white"
              }`}
          >
            <Heart className={`w-4 h-4 ${isBookmarked ? "fill-current" : ""}`} />
          </button>

          {/* Run & Lot Number Overlays */}
           
            {runNo && (
             <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2.5 py-1 rounded-md text-[10px] font-bold shadow-lg backdrop-blur-sm border border-white/10">
    RUN # <span className="text-white ml-0.5">{runNo}</span>
  </div>
            )}
            {lotNo && (
               <div className="absolute bottom-2 right-2 bg-primary/90 text-white px-2.5 py-1 rounded-md text-[10px] font-bold shadow-lg backdrop-blur-sm">
    LOT <span className="text-white ml-0.5">{lotNo}</span>
  </div>
            )}
          
        </div>

        {/* Content */}
        <div className="p-4">
          <h3
            className="font-bold text-white text-[16px] cursor-pointer hover:text-primary transition-colors duration-200"
            onClick={() => navigate(`/${slug}`)}
          >
            {title.length > 25 ? title.slice(0, 25) + "…" : title}
          </h3>

          {/* Specs Grid */}
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-3 gap-x-2 gap-y-3 text-[12px]">
              {/* Mileage */}

              <div className="flex flex-row items-center">
                {/* Mileage SVG */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 22 18"
                  fill="none"
                  className="text-secondary"
                >
                  <path
                    d="M21.7839 11.0194C21.7839 13.4529 21.0682 15.6239 19.6368 17.5324C19.4936 17.7233 19.3028 17.8306 19.0642 17.8545C18.8256 17.8784 18.6109 17.8187 18.4201 17.6756C18.2292 17.5324 18.1219 17.3535 18.098 17.1388C18.0741 16.9241 18.1338 16.7213 18.2769 16.5304C19.5175 14.9081 20.1378 13.0712 20.1378 11.0194C20.1378 9.39715 19.7322 7.89415 18.9211 6.51044C18.1099 5.12672 17.0006 4.02929 15.593 3.21815C14.1854 2.40701 12.6705 2.00144 11.0482 2.00144C9.42593 2.00144 7.911 2.40701 6.50343 3.21815C5.09586 4.02929 3.9865 5.12672 3.17536 6.51044C2.36421 7.89415 1.95864 9.39715 1.95864 11.0194C1.95864 13.0712 2.57893 14.9081 3.8195 16.5304C3.96264 16.7213 4.02229 16.9241 3.99843 17.1388C3.97457 17.3535 3.86721 17.5324 3.67636 17.6756C3.4855 17.8187 3.27079 17.8784 3.03221 17.8545C2.79364 17.8306 2.60279 17.7233 2.45964 17.5324C1.02821 15.6239 0.3125 13.4529 0.3125 11.0194C0.3125 9.11086 0.789643 7.33351 1.74393 5.68736C2.69821 4.04122 3.99843 2.74101 5.64457 1.78672C7.29072 0.832438 9.09193 0.355293 11.0482 0.355293C13.0045 0.355293 14.8057 0.832438 16.4519 1.78672C18.098 2.74101 19.3982 4.04122 20.3525 5.68736C21.3068 7.33351 21.7839 9.11086 21.7839 11.0194ZM16.3445 6.00944C16.5354 6.15258 16.6308 6.34344 16.6308 6.58201C16.6308 6.82058 16.5354 7.01144 16.3445 7.15458L13.6248 9.94586C13.8634 10.3753 13.9826 10.8286 13.9826 11.3057C13.9826 12.1169 13.6964 12.8087 13.1238 13.3813C12.5512 13.9539 11.8594 14.2401 11.0482 14.2401C10.2371 14.2401 9.54522 13.9539 8.97264 13.3813C8.40007 12.8087 8.11379 12.1169 8.11379 11.3057C8.11379 10.4946 8.40007 9.80272 8.97264 9.23015C9.54522 8.65758 10.2371 8.37129 11.0482 8.37129C11.5254 8.37129 11.9786 8.49058 12.4081 8.72915L15.1994 6.00944C15.3425 5.81858 15.5334 5.72315 15.7719 5.72315C16.0105 5.72315 16.2014 5.81858 16.3445 6.00944ZM12.3365 11.3057C12.3365 10.9717 12.2053 10.6854 11.9429 10.4469C11.6804 10.2083 11.3822 10.089 11.0482 10.089C10.7142 10.089 10.416 10.2083 10.1536 10.4469C9.89114 10.6854 9.75993 10.9717 9.75993 11.3057C9.75993 11.6397 9.89114 11.9379 10.1536 12.2004C10.416 12.4628 10.7142 12.594 11.0482 12.594C11.3822 12.594 11.6804 12.4628 11.9429 12.2004C12.2053 11.9379 12.3365 11.6397 12.3365 11.3057Z"
                    fill="currentColor"
                  />
                </svg>
                <span className="px-2 text-[12px] font-semibold text-description">
                  {formatNumber(miles)}
                </span>
              </div>

              {/* Fuel */}
              <div className="flex flex-row items-center">
                {/* Fuel SVG */}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 9 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_3_2662)">
                    <path
                      d="M5 9H0V1C0 0.726667 0.0966666 0.49 0.29 0.29C0.49 0.0966666 0.726667 0 1 0H4C4.27333 0 4.51 0.0966666 4.71 0.29C4.90333 0.49 5 0.726667 5 1V4.5H5.5C5.77333 4.5 6.01 4.59667 6.21 4.79C6.40333 4.99 6.5 5.22667 6.5 5.5V7.75C6.5 7.89 6.54667 8.01 6.64 8.11C6.74 8.20333 6.86 8.25 7 8.25C7.14 8.25 7.26 8.20333 7.36 8.11C7.45333 8.01 7.5 7.89 7.5 7.75V4.15C7.42667 4.19 7.34667 4.21667 7.26 4.23C7.18 4.24333 7.09333 4.25 7 4.25C6.64667 4.25 6.35 4.13 6.11 3.89C5.87 3.64333 5.75 3.34667 5.75 3C5.75 2.73333 5.82333 2.49333 5.97 2.28C6.11667 2.06667 6.31 1.91333 6.55 1.82L5.5 0.77L6.03 0.25L7.88 2.05C8 2.17667 8.09333 2.32333 8.16 2.49C8.22 2.65667 8.25 2.82667 8.25 3V7.75C8.25 8.09667 8.13 8.39333 7.89 8.64C7.65 8.88 7.35333 9 7 9C6.64667 9 6.35 8.88 6.11 8.64C5.87 8.39333 5.75 8.09667 5.75 7.75V5.25H5V9ZM1 1V3.5H4V1H1ZM7 3.5C7.14 3.5 7.26 3.45333 7.36 3.36C7.45333 3.26 7.5 3.14 7.5 3C7.5 2.86 7.45333 2.74 7.36 2.64C7.26 2.54667 7.14 2.5 7 2.5C6.86 2.5 6.74 2.54667 6.64 2.64C6.54667 2.74 6.5 2.86 6.5 3C6.5 3.14 6.54667 3.26 6.64 3.36C6.74 3.45333 6.86 3.5 7 3.5ZM1 4.5V8H4V4.5H1Z"
                      fill="#99A3AC"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3_2662">
                      <rect width="8.25" height="9" fill="white" />
                    </clipPath>
                  </defs>
                </svg>

                <span className="px-2 text-[12px] font-semibold text-description">
                  {typeof fuelType === "object" && fuelType !== null
                    ? fuelType.name
                    : fuelType}
                </span>
              </div>

              {/* Body Type */}
              <div className="flex items-center space-x-2 min-w-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  className="text-secondary flex-shrink-0"
                  viewBox="0 0 256 256"
                >
                  <path
                    d="M240,112H211.31L168,68.69A15.86,15.86,0,0,0,156.69,64H44.28A16,16,0,0,0,31,71.12L1.34,115.56A8.07,8.07,0,0,0,0,120v48a16,16,0,0,0,16,16H33a32,32,0,0,0,62,0h66a32,32,0,0,0,62,0h17a16,16,0,0,0,16-16V128A16,16,0,0,0,240,112ZM44.28,80H156.69l32,32H23ZM64,192a16,16,0,1,1,16-16A16,16,0,0,1,64,192Zm128,0a16,16,0,1,1,16-16A16,16,0,0,1,192,192Zm48-24H223a32,32,0,0,0-62,0H95a32,32,0,0,0-62,0H16V128H240Z"
                    fill="currentColor"
                  />
                </svg>

                <span className="text-[12px] font-semibold text-description truncate">
                  {vehicle?.BodyType?.name ||
                    (bodyType && typeof bodyType === "object"
                      ? bodyType.name
                      : bodyType || "")}
                </span>
              </div>



              {/* Engine */}
              <div className="flex flex-row items-center">
                {/* Engine SVG */}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-secondary"
                >
                  <g clip-path="url(#clip0_1730_33991)">
                    <path
                      d="M13.1456 6.40362V4.6354H15.0927V2.86719H9.64062V4.6354H11.5878V6.40362"
                      stroke="currentColor"
                      stroke-width="0.934628"
                      stroke-miterlimit="10"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M4.96705 12.1496H3.40931V9.49731H1.85156V16.5702H3.40931V13.9179H4.96705"
                      stroke="currentColor"
                      stroke-width="0.934628"
                      stroke-miterlimit="10"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M18.9865 9.05426V11.2645H17.8182V7.6587C17.8182 7.20913 17.5024 6.84399 17.1137 6.84399H9.2467C9.06016 6.84399 8.88024 6.93019 8.74822 7.08359L6.91398 9.49632H5.74567C5.35623 9.49632 4.9668 9.93837 4.9668 10.3804V15.6851C4.9668 16.1351 5.35701 16.5692 5.74567 16.5692H6.91398L8.749 18.9832C8.88102 19.1357 9.06016 19.2215 9.2467 19.2215H14.2529C14.4394 19.2215 14.6186 19.1357 14.7506 18.9832L17.6114 15.6758C17.7442 15.5228 17.8178 15.316 17.8178 15.0998V13.9169H18.9861V16.1271H20.5439V9.05426H18.9865Z"
                      stroke="currentColor"
                      stroke-width="0.934628"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13.9237 12.5879L10.8082 17.4505L11.9765 13.4721H10.0293L11.9765 9.05151H13.9237L11.9765 12.5879H13.9237Z"
                      fill="currentColor"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1730_33991">
                      <rect
                        width="21.0291"
                        height="21.0291"
                        fill="white"
                        transform="matrix(1 0 0 -1 0.683594 21.5559)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <span className="px-2 text-[12px] font-semibold text-description">
                  {vehicle?.engine?.name ||
                    (engineSize && typeof engineSize === "object"
                      ? engineSize.name
                      : engineSize || "")}
                </span>
              </div>

              {/* Cylinder */}
              <div className="flex flex-row items-center">
                {/* Cylinder SVG */}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-secondary"
                >
                  <path
                    d="M14.6187 1.60034C14.9661 1.54206 15.3765 1.59822 15.7065 1.76245L15.8433 1.8396L15.8442 1.84058C15.8639 1.85357 15.8936 1.87829 15.9263 1.90698C15.9616 1.93801 16.0078 1.98008 16.063 2.03101C16.1734 2.13293 16.3232 2.2736 16.5034 2.44604C16.8642 2.79124 17.3495 3.26429 17.896 3.80542L18.896 4.79858C19.1521 5.0552 19.3388 5.24445 19.4761 5.3894C19.7484 5.67706 19.8368 5.79741 19.897 5.92065C20.0743 6.27344 20.1338 6.66199 20.0747 7.01929V7.02026C20.052 7.15075 20.0374 7.26297 19.9849 7.3855C19.932 7.50884 19.8436 7.63774 19.6851 7.82202C19.3696 8.18864 18.7453 8.80779 17.4692 10.0847C16.4484 11.107 15.855 11.6941 15.4917 12.0398C15.1318 12.3823 14.9885 12.4964 14.8696 12.5603L14.8677 12.5613C14.4483 12.7788 14.023 12.8325 13.5972 12.7214H13.5962C13.3863 12.6639 13.2318 12.6038 13.0444 12.4705C12.8625 12.3409 12.6493 12.1423 12.3179 11.8152L11.7661 11.2712L6.95361 16.074L7.01123 16.1892L7.07764 16.3328C7.22022 16.6682 7.27686 17.0109 7.27686 17.4587C7.27684 17.7399 7.2585 17.9587 7.21045 18.1677C7.16237 18.3769 7.08588 18.5714 6.97314 18.8044H6.97217C6.58547 19.6014 5.86111 20.1929 4.98975 20.4226H4.98877C4.56556 20.5305 3.88838 20.5306 3.46631 20.4226H3.46533C1.64925 19.9383 0.687092 17.9582 1.41553 16.2654L1.49072 16.1023C1.57086 15.9382 1.63365 15.8249 1.7124 15.7156C1.79063 15.607 1.88246 15.5053 2.01709 15.363C2.41953 14.9364 2.86556 14.6729 3.43115 14.5095H3.43311C3.63603 14.455 3.92569 14.4248 4.20654 14.4207C4.48624 14.4165 4.77322 14.4375 4.96826 14.4939H4.96729L5.13623 14.5398L5.26416 14.4304C5.36971 14.3343 6.5075 13.2135 7.81494 11.9089L10.1157 9.61304L9.68311 9.17456C9.41768 8.90603 9.24582 8.71824 9.13037 8.54858C9.01192 8.37452 8.95353 8.22237 8.90771 8.03003C8.80575 7.59706 8.88976 7.06841 9.11084 6.69507L9.11279 6.69214H9.11377C9.12747 6.67072 9.15917 6.63441 9.19385 6.59644C9.23238 6.55425 9.28491 6.49779 9.3501 6.42944C9.48102 6.29218 9.66361 6.10437 9.88428 5.87964C10.326 5.42976 10.9227 4.83028 11.5679 4.19019C12.639 3.12504 13.2299 2.5396 13.5786 2.20972C13.9207 1.8861 14.0416 1.79626 14.1655 1.74292L14.3921 1.65601C14.4737 1.62966 14.5559 1.60832 14.6187 1.59937V1.60034ZM4.93994 15.4275C4.30037 15.2101 3.60179 15.2937 3.05615 15.6589H3.05518C2.92841 15.7425 2.77505 15.8831 2.63623 16.0388C2.4973 16.1947 2.3802 16.357 2.31982 16.4812V16.4822C1.83075 17.4722 2.139 18.6249 3.05615 19.24H3.05518C3.2503 19.3701 3.41811 19.4526 3.59814 19.5037C3.77879 19.5549 3.97868 19.5759 4.2417 19.5759C4.50448 19.5759 4.70451 19.555 4.88525 19.5037C5.06542 19.4525 5.23273 19.3695 5.42725 19.24C6.04539 18.8184 6.38916 18.1902 6.38916 17.4568C6.38906 16.8932 6.20075 16.4008 5.82666 16.0046C5.55389 15.7185 5.29198 15.5473 4.93994 15.4275ZM8.39307 12.6101C7.72024 13.2815 7.10872 13.8935 6.66553 14.3406C6.444 14.5641 6.26488 14.7462 6.14111 14.8738C6.09847 14.9177 6.06227 14.955 6.03369 14.9851C6.04295 14.9924 6.05284 15.0002 6.06299 15.0085C6.11571 15.052 6.18368 15.1117 6.25244 15.1765H6.25146L6.42041 15.323L8.78076 12.9666L11.1226 10.6169L10.9448 10.4392L10.7573 10.2576L8.39307 12.6101ZM12.3062 4.74487C12.2395 4.80941 12.1431 4.90294 12.0259 5.01831C11.7916 5.24895 11.4715 5.56659 11.1235 5.91382C10.5165 6.51901 10.2004 6.83637 10.0288 7.02612C9.94313 7.12088 9.89762 7.17911 9.87061 7.22241C9.84598 7.2619 9.83448 7.29051 9.82275 7.33081L9.82373 7.33179C9.78917 7.45351 9.77397 7.53684 9.77393 7.61597C9.77393 7.69517 9.78914 7.7783 9.82373 7.90015H9.82275C9.83407 7.9384 9.84532 7.96671 9.87842 8.01538C9.91502 8.06915 9.97895 8.14639 10.1001 8.2771C10.3417 8.53773 10.7946 8.99418 11.6704 9.87183L12.8384 11.0349C13.1261 11.3165 13.3255 11.5032 13.4751 11.6267C13.6236 11.7493 13.714 11.8013 13.7856 11.8259C13.8555 11.8499 13.9161 11.8503 14.0239 11.8503C14.1315 11.8482 14.1987 11.8457 14.2661 11.8259C14.3321 11.8066 14.4069 11.7687 14.5171 11.6843C14.7428 11.5116 15.0879 11.1697 15.7798 10.4792L16.938 9.32202L14.6392 7.0271H14.6382C13.9856 6.37172 13.3887 5.77681 12.9546 5.34546C12.7376 5.1299 12.561 4.95514 12.439 4.83374C12.3944 4.78943 12.3572 4.75198 12.3276 4.72241C12.3206 4.72918 12.314 4.73725 12.3062 4.74487ZM12.9722 4.06909L17.5874 8.67456L18.1509 8.11304L16.1831 6.15796V6.15894C15.0507 5.03641 13.9749 3.96353 13.7866 3.78296L13.7856 3.78198L13.519 3.52222L12.9722 4.06909ZM15.2876 2.56226C14.9587 2.39873 14.5383 2.48374 14.2524 2.79077L14.2495 2.79468L14.1665 2.87671L18.7993 7.46948L18.8921 7.38159C19.0032 7.27467 19.0759 7.17604 19.1216 7.07397C19.167 6.97236 19.188 6.86043 19.188 6.72339C19.188 6.61077 19.1883 6.55009 19.1655 6.4812C19.1427 6.41196 19.0929 6.32561 18.9731 6.1814C18.7317 5.89078 18.2413 5.40673 17.2368 4.41284V4.41187C16.5676 3.74887 16.105 3.29596 15.7944 3.00171C15.6391 2.85449 15.5226 2.74803 15.438 2.67554C15.3959 2.63947 15.363 2.61239 15.3374 2.59351C15.3246 2.58408 15.314 2.57695 15.3062 2.57202C15.3026 2.56978 15.2996 2.56835 15.2974 2.56714C15.2962 2.56653 15.295 2.56549 15.2944 2.56519H15.2935L15.2876 2.56226Z"
                    fill="currentColor"
                    stroke="currentColor"
                    stroke-width="0.233657"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.9655 7.92444C12.7354 8.02765 12.5454 8.31201 12.5454 8.53213C12.5454 8.77647 12.7586 9.06083 13.0045 9.15561C13.5587 9.36836 14.0811 8.78489 13.8363 8.23197C13.6853 7.92444 13.2737 7.77384 12.9655 7.92444Z"
                    fill="currentColor"
                  />
                </svg>
                <span className="px-2 text-[12px] font-semibold text-description">
                  {typeof cylinder === "object" && cylinder !== null
                    ? cylinder.name
                    : cylinder} Cyl
                </span>
              </div>

              {/* Drive Line */}
              <div className="flex flex-row items-center">
                {/* Driveline SVG */}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-secondary"
                >
                  <path
                    d="M10.4062 3.83338H5.23958C5.00347 3.83338 4.80556 3.75352 4.64583 3.5938C4.48611 3.43408 4.40625 3.23616 4.40625 3.00005C4.40625 2.76394 4.48611 2.56602 4.64583 2.4063C4.80556 2.24658 5.00347 2.16672 5.23958 2.16672H10.4062L10.0729 1.83338C9.90625 1.66672 9.82639 1.47227 9.83333 1.25005C9.84028 1.02783 9.92014 0.833384 10.0729 0.666717C10.2396 0.50005 10.4375 0.413245 10.6667 0.4063C10.8958 0.399356 11.0937 0.479217 11.2604 0.645884L13.0312 2.41672C13.1979 2.58338 13.2812 2.77783 13.2812 3.00005C13.2812 3.22227 13.1979 3.41672 13.0312 3.58338L11.2812 5.33338C11.1146 5.50005 10.9201 5.57991 10.6979 5.57297C10.4757 5.56602 10.2812 5.48616 10.1146 5.33338C9.94792 5.16672 9.86111 4.9688 9.85417 4.73963C9.84722 4.51047 9.92708 4.31255 10.0937 4.14588L10.4062 3.83338ZM1.94792 15.5001H13.6146V11.3334H1.94792V15.5001ZM4.03125 14.6667C4.39236 14.6667 4.69097 14.5487 4.92708 14.3126C5.16319 14.0764 5.28125 13.7778 5.28125 13.4167C5.28125 13.0556 5.16319 12.757 4.92708 12.5209C4.69097 12.2848 4.39236 12.1667 4.03125 12.1667C3.67014 12.1667 3.37153 12.2848 3.13542 12.5209C2.89931 12.757 2.78125 13.0556 2.78125 13.4167C2.78125 13.7778 2.89931 14.0764 3.13542 14.3126C3.37153 14.5487 3.67014 14.6667 4.03125 14.6667ZM11.5312 14.6667C11.8924 14.6667 12.191 14.5487 12.4271 14.3126C12.6632 14.0764 12.7812 13.7778 12.7812 13.4167C12.7812 13.0556 12.6632 12.757 12.4271 12.5209C12.191 12.2848 11.8924 12.1667 11.5312 12.1667C11.1701 12.1667 10.8715 12.2848 10.6354 12.5209C10.3993 12.757 10.2812 13.0556 10.2812 13.4167C10.2812 13.7778 10.3993 14.0764 10.6354 14.3126C10.8715 14.5487 11.1701 14.6667 11.5312 14.6667ZM3.48958 7.16672L3.82292 7.50005C3.98958 7.66672 4.06944 7.86116 4.0625 8.08338C4.05556 8.30561 3.97569 8.50005 3.82292 8.66672C3.65625 8.83338 3.45833 8.92019 3.22917 8.92713C3 8.93408 2.80208 8.85422 2.63542 8.68755L0.864583 6.91672C0.697917 6.75005 0.614583 6.55561 0.614583 6.33338C0.614583 6.11116 0.697917 5.91672 0.864583 5.75005L2.63542 3.97922C2.80208 3.81255 3 3.73269 3.22917 3.73963C3.45833 3.74658 3.65625 3.83338 3.82292 4.00005C3.97569 4.16672 4.05556 4.36116 4.0625 4.58338C4.06944 4.80561 3.98958 5.00005 3.82292 5.16672L3.48958 5.50005H12.0521C12.4132 5.50005 12.7396 5.60422 13.0312 5.81255C13.3229 6.02088 13.5243 6.29172 13.6354 6.62505L15.1979 11.0626C15.2257 11.1459 15.2465 11.2362 15.2604 11.3334C15.2743 11.4306 15.2812 11.5278 15.2812 11.6251V17.5834C15.2812 17.9306 15.1597 18.2257 14.9167 18.4688C14.6736 18.7119 14.3785 18.8334 14.0312 18.8334C13.684 18.8334 13.3889 18.7119 13.1458 18.4688C12.9028 18.2257 12.7812 17.9306 12.7812 17.5834V17.1667H2.78125V17.5834C2.78125 17.9306 2.65972 18.2257 2.41667 18.4688C2.17361 18.7119 1.87847 18.8334 1.53125 18.8334C1.18403 18.8334 0.888889 18.7119 0.645833 18.4688C0.402778 18.2257 0.28125 17.9306 0.28125 17.5834V11.3334C0.28125 10.8751 0.444444 10.4827 0.770833 10.1563C1.09722 9.82991 1.48958 9.66672 1.94792 9.66672H12.9479L12.0729 7.16672H3.48958Z"
                    fill="currentColor"
                  />
                </svg>
                <span className="px-2 text-[12px] font-semibold text-description">
                  {vehicle.DriveLineType?.name || vehicle.driveLineType?.name}
                </span>
              </div>
            </div>
          </div>

          <div className="my-3 border-t border-default" />

          {/* Price & Action */}
          <div className="flex justify-between items-center">
            <div>
              {isBuyNowVehicle ? (
                <div>
                  <div className="flex flex-col">
                    <span className="text-xs uppercase text-description">
                      {translate("vehicleCard.labels.currentBidding")}
                    </span>
                    <span className="text-xl font-bold text-primary mt-0.5">
                      {displayPriceWithSymbol(convertPrice(Number(buyNowPrice || 0)))}
                    </span>
                    {/* <span className="text-sm text-gray-500">Buy Now</span> */}
                  </div>
                  {buyNowEndTime && (
                    <div className="mt-1 text-xs">
                      <span className="text-gray-500">{translate("vehicleCard.labels.endsIn")}</span>
                      <span
                        className={`ml-1.5 font-medium ${buyNowTimeLeft <= 0
                          ? "text-red-600"
                          : buyNowTimeLeft < 3600000
                            ? "text-orange-600"
                            : "text-gray-600"
                          }`}
                      >
                        {formatTimeRemaining(buyNowTimeLeft)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col">
                  <span className="text-xs uppercase text-description">
                    {translate("vehicleCard.labels.currentBidding")}
                  </span>
                  <span className="text-xl font-bold text-primary mt-0.5">
                    {displayPriceWithSymbol(convertPrice(Number(latestBid || 0)))}
                  </span>
                </div>
              )}
            </div>

            {/* Right Side - Action Button */}
            <div className="ml-4">
              {isBuyNowExpired ? (
                <div className="bg-gray-500 rounded text-white py-1 px-3 font-semibold cursor-not-allowed flex items-center justify-center gap-1 whitespace-nowrap">
                  {translate("vehicleCard.status.buyNowExpired")}
                </div>
              ) : isBuyNowVehicle ? (
                <button
                  onClick={handleBuyNow}
                  className="bg-black text-white text-[12px] font-semibold rounded-md py-2 px-4 flex items-center justify-center gap-2 hover:bg-gray-600 transition-colors duration-200 whitespace-nowrap"
                >
                  {translate("vehicleCard.buttons.buyNow")}
                </button>
              ) : isFuture ? (
                <div className="bg-soft rounded text-primary py-1 px-3 font-semibold flex items-center justify-center gap-1 whitespace-nowrap hover:bg-primary hover:text-white">
                  {translate("vehicleCard.status.future")}
                </div>
              ) : isVehicleSold ? (
                <div className="bg-red-600 text-white text-[12px] font-semibold rounded-md py-2 px-4 flex items-center justify-center gap-2 cursor-not-allowed whitespace-nowrap opacity-90 select-none">
                  {translate("vehicleCard.status.vehicleSold")}
                </div>
              ) : (
                <button
                  onClick={handleBidNow}
                  className="bg-primary-soft text-white text-[12px] font-semibold rounded-md py-2 px-4 flex items-center justify-center gap-2 hover:bg-primary transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {isLive
                    ? translate("vehicleCard.buttons.bidNow") || "Bid Now"
                    : translate("vehicleCard.buttons.preBid") || "Pre-Bid"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pre-bid Modal */}
      {showBidNowPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-bold">{translate("vehicleCard.modal.preBidSummary")}</h3>
                <button onClick={closeBidNowPopup} className="text-gray-500 hover:text-gray-800 text-2xl">
                  ×
                </button>
              </div>

              <div className="mb-5">
                <h4 className="font-semibold mb-2">{title}</h4>
                <div className={`flex justify-between text-sm ${directionalRowClass}`}>
                  <span>
                    Lot: <strong className="text-primary">{lotNo}</strong>
                  </span>
                  {runNo && (
                    <span>
                      Run: <strong className="text-primary">{runNo}</strong>
                    </span>
                  )}
                </div>
              </div>

              <BidInformation
                vehicle={updatedVehicle}
                prebidTimeLeft={prebidTimeLeft}
                bidAmount={bidAmount}
                setBidAmount={setBidAmount}
                bidIncrement={bidIncrement}
                isSubmittingBid={isSubmitting}
                handlePreBidSubmit={handleBidNowSubmit}
                getMinBidAmount={() => {
                  const base = Number(updatedVehicle.latestBid) || startingBid;
                  return base + bidIncrement;
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Buy Now Confirmation Modal */}
      {showBuyNowModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-bold text-primary">Confirm Buy Now</h3>
              <button onClick={handleBuyNowModalClose} className="text-2xl text-gray-500 hover:text-gray-800">
                ×
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-4 mb-5">
                <img src={image} alt={title} className="w-20 h-16 object-cover rounded" />
                <div>
                  <h4 className="font-semibold text-lg">{title}</h4>
                  <p className="text-sm text-gray-600">
                    Lot: <span className="font-medium text-primary">{lotNo}</span>
                    {runNo && <> • Run: <span className="font-medium text-primary">{runNo}</span></>}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg text-center mb-6">
                <p className="text-sm text-gray-600 mb-1">Buy Now Price</p>
                <p className="text-3xl font-bold text-primary">
                  {displayPriceWithSymbol(convertPrice(Number(buyNowPrice || 0)))}
                </p>
              </div>

              <p className="text-center text-gray-700 mb-6">
                Are you sure you want to purchase this vehicle for{" "}
                <strong className="text-primary">
                  {displayPriceWithSymbol(convertPrice(Number(buyNowPrice || 0)))}
                </strong>
                ?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleBuyNowModalClose}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBuyNowConfirm}
                  className="flex-1 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium"
                >
                  Confirm Purchase
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleCard;